/**
 * Reads a single space (detail-shape) by id, fetching from the BE on mount /
 * when params change. Wraps the shared `useQuery` for fetch lifecycle (mount,
 * key-change, abort on unmount) but keeps the spaces sweet-state store as
 * the canonical home for the data — so optimistic updates from elsewhere
 * (e.g. a successful PUT in a sibling `useUpdateSpace`, or a `patchSpace`
 * from a side-nav rename) reflect here instantly without a refetch.
 *
 * Public API is unchanged from the previous local-`useState` implementation.
 */
import { useCallback } from 'react';

import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';

import {
	useSpaceById,
	useSpaceDetailError,
	useSpaceDetailLoading,
	useSpaceStoreActions,
} from '../../controllers/space-store/hooks';
import type { SpaceResponse } from '../../controllers/space-store/types';

type UseGetSpaceProps = {
	spaceId: string;
	cloudId: string;
	product: string;
	experienceId: string;
	/** When false the hook will not fire an HTTP request. Defaults to true. */
	enabled?: boolean;
};

type UseGetSpaceResult = {
	space: SpaceResponse | null;
	isLoading: boolean;
	error: Error | null;
	refetch: () => Promise<void>;
};

export const useGetSpace = ({
	spaceId,
	cloudId,
	product,
	experienceId,
	enabled = true,
}: UseGetSpaceProps): UseGetSpaceResult => {
	const { fetchSpace } = useSpaceStoreActions();
	const space = useSpaceById(spaceId);
	const isLoading = useSpaceDetailLoading(spaceId);
	const error = useSpaceDetailError(spaceId);

	const { refetch: refetchQuery } = useQuery<void>({
		queryKey: ['rovo-spaces:getSpace', spaceId, cloudId, product, experienceId],
		queryFn: () => fetchSpace({ spaceId, cloudId, product, experienceId }),
		enabled,
	});

	const refetch = useCallback(async () => {
		await refetchQuery();
	}, [refetchQuery]);

	return { space: space ?? null, isLoading, error, refetch };
};
